/**
 * OtaUpdateModal
 * 
 * OTA 관련 모든 팝업을 통합 관리:
 * 
 * 1. 네이티브 앱 강제 업데이트 (닫기 불가 → 스토어 이동)
 * 2. 네이티브 앱 권고 업데이트 (닫기 가능 → 스토어 이동)
 * 3. OTA 번들 flexible 업데이트 (닫기 가능 → 즉시 적용)
 * 
 * silent 모드는 UI 없이 자동 처리됨 (OtaUpdateService에서 처리)
 * 
 * Lingui 대신 자체 번역 사용 — 앱 초기화 전에도 올바른 언어로 표시
 */

import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { otaService } from '../../services/Utils/OtaUpdateService';
import package_json from '../../../package.json';

const pkg_version = package_json.version;

// ── 자체 언어 감지 & 번역 (OtaUpdateService와 동일 방식) ──

const modalLang = (() => {
  const l = ((navigator.languages && navigator.languages[0]) || localStorage.getItem('app-lang') || navigator.language || '').slice(0, 2);
  return l === 'ko' ? 'ko' : l === 'ja' ? 'ja' : 'en';
})();

const modalTexts = {
  ko: {
    forcedTitle: '앱 업데이트가 필요합니다',
    forcedDesc: '보안 및 안정성을 위해 최신 버전으로 업데이트해 주세요.',
    recommendedTitle: '새로운 버전이 있습니다',
    recommendedDesc: '더 나은 경험을 위해 최신 버전으로 업데이트를 권장합니다.',
    flexibleTitle: '업데이트 준비 완료',
    flexibleDesc: '새로운 개선 사항이 있습니다. 지금 적용하시겠습니까?',
    update: '업데이트하기',
    applyNow: '지금 적용',
    updating: '업데이트 중...',
    later: '나중에',
  },
  ja: {
    forcedTitle: 'アプリのアップデートが必要です',
    forcedDesc: 'セキュリティと安定性のため、最新バージョンにアップデートしてください。',
    recommendedTitle: '新しいバージョンがあります',
    recommendedDesc: 'より良い体験のため、最新バージョンへのアップデートをおすすめします。',
    flexibleTitle: 'アップデート準備完了',
    flexibleDesc: '新しい改善があります。今すぐ適用しますか？',
    update: 'アップデートする',
    applyNow: '今すぐ適用',
    updating: 'アップデート中...',
    later: 'あとで',
  },
  en: {
    forcedTitle: 'App Update Required',
    forcedDesc: 'Please update to the latest version for security and stability.',
    recommendedTitle: 'New Version Available',
    recommendedDesc: 'We recommend updating to the latest version for a better experience.',
    flexibleTitle: 'Update Ready',
    flexibleDesc: 'New improvements are available. Apply now?',
    update: 'Update',
    applyNow: 'Apply Now',
    updating: 'Updating...',
    later: 'Later',
  },
} as const;

const t = modalTexts[modalLang];

type ModalMode =
  | { kind: 'none' }
  | { kind: 'native-forced'; storeUrl: string }
  | { kind: 'native-recommended'; storeUrl: string }
  | { kind: 'bundle-flexible'; version: string };

export default function OtaUpdateModal() {
  const [mode, setMode] = useState<ModalMode>({ kind: 'none' });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const run = async () => {
      await otaService.init(); // 웹: 즉시 resolve, 네이티브: OTA 체크
      if (Capacitor.getPlatform() === 'web') return;
      await otaService.runUpdateFlow();

      // UI가 필요한 경우만 모달 표시
      const native = otaService.getNativeUpdateStatus();
      if (native.type === 'forced') {
        setMode({ kind: 'native-forced', storeUrl: native.storeUrl });
        return;
      }
      if (native.type === 'recommended') {
        setMode({ kind: 'native-recommended', storeUrl: native.storeUrl });
        return;
      }

      // 번들 flexible 모드 (silent/forced는 runUpdateFlow에서 자동 처리됨)
      const bundle = otaService.getBundleUpdateStatus();
      if (bundle.type === 'available' && bundle.bundle.mode === 'flexible') {
        setMode({ kind: 'bundle-flexible', version: bundle.bundle.fullVersion });
      }
    };

    run();
  }, []);

  if (mode.kind === 'none') return null;

  const handleStoreRedirect = (url: string) => {
    const platform = Capacitor.getPlatform();
    console.log(`[OTA] handleStoreRedirect — platform: ${platform}, url: ${url}`);

    if (platform === 'ios') {
      const deepLink = url.replace(/^https?:\/\//, 'itms-apps://');
      console.log(`[OTA] iOS deepLink: ${deepLink}`);
      window.location.href = deepLink;
    } else if (platform === 'android') {
      const match = url.match(/[?&]id=([^&]+)/);
      if (match) {
        const deepLink = `market://details?id=${match[1]}`;
        console.log(`[OTA] Android deepLink: ${deepLink}`);
        window.location.href = deepLink;
      } else {
        console.log(`[OTA] Android fallback URL: ${url}`);
        window.location.href = url;
      }
    } else {
      window.open(url, '_blank');
    }
  };

  const handleBundleApply = async () => {
    setUpdating(true);
    const success = await otaService.downloadAndApply();
    if (!success) {
      setUpdating(false);
      setMode({ kind: 'none' });
    }
  };

  const dismiss = () => {
    setMode({ kind: 'none' });
    // 네이티브 업데이트를 나중에 하더라도 번들은 백그라운드로 받아둠
    const bundle = otaService.getBundleUpdateStatus();
    if (bundle.type === 'available') {
      otaService.downloadOnly();
    }
  };

  // ── 모달 내용 결정 ──

  let title = '';
  let description = '';
  let buttonText = '';
  let buttonBg = 'linear-gradient(135deg, #667eea, #764ba2)';
  let onButtonClick: () => void = dismiss;
  let canDismiss = true;

  switch (mode.kind) {
    case 'native-forced':
      title = t.forcedTitle;
      description = t.forcedDesc;
      buttonText = t.update;
      buttonBg = 'linear-gradient(135deg, #FF6B6B, #EE5A24)';
      onButtonClick = () => handleStoreRedirect(mode.storeUrl);
      canDismiss = false;
      break;

    case 'native-recommended':
      title = t.recommendedTitle;
      description = t.recommendedDesc;
      buttonText = t.update;
      onButtonClick = () => handleStoreRedirect(mode.storeUrl);
      break;

    case 'bundle-flexible':
      title = t.flexibleTitle;
      description = t.flexibleDesc;
      buttonText = updating ? t.updating : t.applyNow;
      buttonBg = 'linear-gradient(135deg, #43E97B, #38F9D7)';
      onButtonClick = handleBundleApply;
      break;
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        background: 'rgb(var(--background, 255 255 255))', borderRadius: '20px',
        padding: '32px 28px', maxWidth: '340px',
        width: 'calc(100% - 48px)', textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* 로고 */}
        <img src="assets/img/logo.png" alt="NEXTPOD" style={{
          width: '80px', height: 'auto', margin: '0 auto 20px', display: 'block',
        }} />

        {/* 제목 */}
        <h2 style={{
          fontSize: '20px', fontWeight: '700', color: 'rgb(var(--text-title, 26 26 26))',
          margin: '0 0 12px', lineHeight: '1.3',
        }}>
          {title}
        </h2>

        {/* 설명 */}
        <p style={{
          fontSize: '14px', color: 'rgb(var(--text-body, 102 102 102))',
          margin: '0 0 28px', lineHeight: '1.6',
        }}>
          {description}
        </p>

        {/* 주요 버튼 */}
        <button
          onClick={onButtonClick}
          disabled={updating}
          style={{
            width: '100%', padding: '14px 0', borderRadius: '12px',
            border: 'none', background: buttonBg, color: '#ffffff',
            fontSize: '16px', fontWeight: '600', cursor: updating ? 'wait' : 'pointer',
            marginBottom: canDismiss ? '12px' : '0',
            opacity: updating ? 0.7 : 1,
          }}
        >
          {buttonText}
        </button>

        {/* 나중에 버튼 */}
        {canDismiss && (
          <button
            onClick={dismiss}
            style={{
              width: '100%', padding: '12px 0', borderRadius: '12px',
              border: 'none', background: 'transparent',
              color: 'rgb(var(--text-body, 153 153 153))', fontSize: '14px', fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            {t.later}
          </button>
        )}

        {/* 버전 */}
        <p style={{ fontSize: '11px', color: 'rgb(var(--text-body, 204 204 204))', margin: '16px 0 0', opacity: 0.5 }}>
          v{pkg_version}
        </p>
      </div>
    </div>
  );
}
